// Libs
import chroma from "chroma-js";
import { max, maxBy, min, minBy } from "lodash";

// Utils
import { customPalette } from "src/theme/customPalette";
import { INFINITE_TAG } from "src/utils/trigger";

// Types
import type { LimitEvolution } from "src/components-ds/heat-map/utils";
import { HourlyMetricsDashboardEnum } from "src/components-ds/hourly-views/half-hourly-smart-scheduler/utils/types";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { useQueryString } from "src/design-system/core/hooks";
import type {
    HourlyMetricsByHourAndDow,
    HourlyMetricsByHourAndDowExtended,
} from "src/types/organization/HourlyMetricsByHourAndDow";
import { fixSimplePercentValue } from "src/utils/formatters";
import { NO_SCENARIO_ID } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard";

export type ScaleMode = "continuous" | "steps";

// COLORS
export const HOURLY_METRICS_CONTINUOUS_COLORS = {
    0: customPalette.other.lightGrey,
    75: customPalette.primary.main,
    100: customPalette.other.darkBlue,
};

const HOURLY_METRICS_CONTINUOUS_NEGATIVE_COLORS = {
    0: "#EE3E7A66",
    100: "#EE3E7A",
};

const HOURLY_METRICS_CONTINUOUS_POSITIVE_COLORS = {
    0: "#2AB6A780",
    100: "#2AB6A7",
};

// SCALES
const scale = chroma
    .scale(Object.values(HOURLY_METRICS_CONTINUOUS_COLORS))
    .domain(Object.keys(HOURLY_METRICS_CONTINUOUS_COLORS));

const scaleNegative = chroma
    .scale(Object.values(HOURLY_METRICS_CONTINUOUS_NEGATIVE_COLORS))
    .domain(Object.keys(HOURLY_METRICS_CONTINUOUS_NEGATIVE_COLORS));

const scalePositive = chroma
    .scale(Object.values(HOURLY_METRICS_CONTINUOUS_POSITIVE_COLORS))
    .domain(Object.keys(HOURLY_METRICS_CONTINUOUS_POSITIVE_COLORS));

// STEPS
const steps = scale.colors(11);
const stepsNegative = scaleNegative.colors(7);
const stepsPositive = scalePositive.colors(7);

export const HOURLY_METRICS_STEPS_COLORS = {
    "0": steps[0],
    "0,10": steps[1],
    "10,20": steps[2],
    "20,30": steps[3],
    "30,40": steps[4],
    "40,50": steps[5],
    "50,60": steps[6],
    "60,70": steps[7],
    "70,80": steps[8],
    "80,90": steps[9],
    "90,100": steps[10],
};

export const HOURLY_METRICS_EVOLUTION_STEPS_NEGATIVE_COLORS = {
    "0.75,1": stepsNegative[5],
    "0.60,0.75": stepsNegative[4],
    "0.45,0.60": stepsNegative[3],
    "0.30,0.45": stepsNegative[2],
    "0.15,0.30": stepsNegative[1],
    "0,0.15": stepsNegative[0],
};

export const HOURLY_METRICS_EVOLUTION_STEPS_POSITIVE_COLORS = {
    "0,0.15": stepsPositive[0],
    "0.15,0.30": stepsPositive[1],
    "0.30,0.45": stepsPositive[2],
    "0.45,0.60": stepsPositive[3],
    "0.60,0.75": stepsPositive[4],
    "0.75,1": stepsPositive[5],
};

const normalizeForColor = (value: number, min: number, max: number): number => {
    if (max - min === 0) {
        return 0;
    }
    if (value === 0) {
        return 0;
    }
    return (value - min) / (max - min);
};

/**
 * Hook to know the selected mode with URI
 * @returns Current mode
 */
export function useScaleMode(): { mode: ScaleMode } {
    const [mode] = useQueryString("hourly-metrics-scale");

    return { mode: ["continuous", "steps"].includes(mode) ? (mode as ScaleMode) : "steps" };
}

/**
 * Get the correct color in scale for a percentage
 * @param percentage Percentage between [0, 100]
 * @returns Color
 */
function getContinuousColor(percentage: number): string {
    return scale(percentage).toString();
}

/**
 * Get the correct color in steps for a percentage
 * @param percentage Percentage between [0, 100]
 * @returns Color
 */
function getStepColor(percentage: number, value: number): string {
    if (percentage === 0 && !value) {
        return HOURLY_METRICS_STEPS_COLORS["0"];
    }
    if (percentage === 100) {
        return HOURLY_METRICS_STEPS_COLORS["90,100"];
    }

    const color = Object.entries(HOURLY_METRICS_STEPS_COLORS)
        .slice(1)
        .find(([range]) => {
            const [min, max] = range.split(",").map((n) => Number.parseInt(n, 10));

            return percentage >= min && percentage < max;
        });

    if (color === undefined || color[1] === undefined) {
        return customPalette.other.lightGrey;
    }
    return color[1];
}

export function getStepColorEvolution(percentage: number | undefined, isNegative: boolean): string {
    const currentStepsColor =
        isNegative === true
            ? HOURLY_METRICS_EVOLUTION_STEPS_NEGATIVE_COLORS
            : HOURLY_METRICS_EVOLUTION_STEPS_POSITIVE_COLORS;

    if (percentage === 0) {
        return customPalette.other.lightGrey;
    }
    if (percentage === 1 || percentage === undefined) {
        return currentStepsColor["0.75,1"];
    }

    const color = Object.entries(currentStepsColor).find(([range]) => {
        const [min, max] = range.split(",").map((n) => Number.parseFloat(n));
        return percentage >= min && percentage < max;
    });

    if (color === undefined || color[1] === undefined) {
        return customPalette.other.lightGrey;
    }
    return color[1];
}

/**
 * Get the correct color in steps for a percentage
 * @param percentage Percentage between [0, 100]
 * @param mode Mode to compute color
 * @returns Color
 */
function getColor(percentage: number, mode: ScaleMode, value: number): string {
    if (mode === "continuous") {
        return getContinuousColor(percentage);
    }

    return getStepColor(percentage, value);
}

export function getColorForVolume(
    mode: ScaleMode,
    hourlyMetricsByHourAndDow: HourlyMetricsByHourAndDowExtended,
    metric: HourlyMetricsDashboardEnum,
    maxFormattedMetric: number,
    minFormattedMetric: number
): string {
    let metricPerDay: string;
    let metricPerWeek: string;
    // OTHERS
    switch (metric) {
        case HourlyMetricsDashboardEnum.AverageBasket: {
            return getColor(
                hourlyMetricsByHourAndDow?.basketPercentageOfWeek / 100 || 0,
                mode,
                hourlyMetricsByHourAndDow?.[metric]
            );
        }
        case HourlyMetricsDashboardEnum.AverageBasketExVat: {
            return getColor(
                hourlyMetricsByHourAndDow?.basketPercentageOfWeekExVat / 100 || 0,
                mode,
                hourlyMetricsByHourAndDow?.[metric]
            );
        }
        case HourlyMetricsDashboardEnum.SumProfit:
        case HourlyMetricsDashboardEnum.ProfitPercentageOfDay: {
            return getColor(
                hourlyMetricsByHourAndDow?.profitPercentageOfDay / 100 >= 1
                    ? hourlyMetricsByHourAndDow?.profitPercentageOfWeek / 100 || 0
                    : 0,
                mode,
                normalizeForColor(
                    hourlyMetricsByHourAndDow?.[metric] as number,
                    minFormattedMetric / 100,
                    maxFormattedMetric / 100
                ) * 100
            );
        }
        case HourlyMetricsDashboardEnum.CountTicket:
        case HourlyMetricsDashboardEnum.TicketPercentageOfDay: {
            metricPerDay = "ticketPercentageOfDay";
            metricPerWeek = "ticketPercentageOfWeek";
            break;
        }
        case HourlyMetricsDashboardEnum.ExvatPercentageOfDay: {
            metricPerDay = "exvatPercentageOfDay";
            metricPerWeek = "exvatPercentageOfWeek";
            break;
        }
        case HourlyMetricsDashboardEnum.InclVat:
        case HourlyMetricsDashboardEnum.InclvatPercentageOfDay:
        default:
            metricPerDay = "inclvatPercentageOfDay";
            metricPerWeek = "inclvatPercentageOfMaxWeek";
    }
    return getColor(
        hourlyMetricsByHourAndDow?.[metricPerDay] / 100 >= 1
            ? hourlyMetricsByHourAndDow?.[metricPerWeek] / 100 || 0
            : 0,
        mode,
        hourlyMetricsByHourAndDow?.[metric] as number
    );
}

export function getColorForPercentage(
    mode: ScaleMode,
    hourlyMetricsByHourAndDow: HourlyMetricsByHourAndDowExtended,
    metric: HourlyMetricsDashboardEnum,
    maxFormattedMetric: number
): string {
    const currentValue = Number.parseInt(hourlyMetricsByHourAndDow?.[metric]?.toString(), 10) / 100;
    const percentage = normalizeForColor(currentValue, 0, maxFormattedMetric) * 100;
    const normalisePercentage = Math.min(percentage, 100);
    return getColor(normalisePercentage || 0, mode, hourlyMetricsByHourAndDow?.[metric] as number);
}

export function getColorForEvolution(
    hourlyMetricsByHourAndDow: HourlyMetricsByHourAndDowExtended,
    metric: HourlyMetricsDashboardEnum,
    limitEvolutions: LimitEvolution
): string {
    if (!hourlyMetricsByHourAndDow) {
        return customPalette.other.lightGrey;
    }

    // Check if the metric exists in the data
    if (
        hourlyMetricsByHourAndDow[metric] === undefined ||
        hourlyMetricsByHourAndDow[metric] === null
    ) {
        return customPalette.other.lightGrey;
    }

    const currentValue = fixSimplePercentValue(
        Number.parseFloat(hourlyMetricsByHourAndDow[metric].toString()),
        undefined
    );

    const minValue = limitEvolutions.min.value;
    const maxValue = limitEvolutions.max.value;

    // INFINITE MANAGEMENT
    if (hourlyMetricsByHourAndDow[metric]?.toString()?.includes(INFINITE_TAG)) {
        // NEGATIVE INFINITE
        if (hourlyMetricsByHourAndDow[metric]?.toString()?.includes("-")) {
            return getStepColorEvolution(undefined, true);
        }
        // POSITIVE INFINITE
        else {
            return getStepColorEvolution(undefined, false);
        }
    }

    // NEGATIVE VALUE
    if (currentValue?.toString()?.includes("-")) {
        const negativeNormalization = normalizeForColor(
            Math.abs(currentValue),
            0,
            Math.abs(minValue)
        );
        return getStepColorEvolution(negativeNormalization, true);
    }
    // POSITIVE VALUE
    else {
        const positiveNormalization = normalizeForColor(currentValue, 0, maxValue);
        return getStepColorEvolution(positiveNormalization, false);
    }
}

const COLOR_HIGHLIGHT_PINK = "#EA548A";
const COLOR_HIGHLIGHT_ORANGE = "#F18A35";
const COLOR_HIGHLIGHT_YELLOW = "#FFD000";
const COLOR_HIGHLIGHT_PURPLE = "#B178F8";
const COLOR_HIGHLIGHT_TURQUOISE = "#54C5B9";
const COLOR_HIGHLIGHT_BLUE = "#6172FD";

const MAPPER_COLOR_HIGHLIGHT = {
    0: [],
    1: [COLOR_HIGHLIGHT_PINK],
    2: [COLOR_HIGHLIGHT_PINK, COLOR_HIGHLIGHT_BLUE],
    3: [COLOR_HIGHLIGHT_PINK, COLOR_HIGHLIGHT_ORANGE, COLOR_HIGHLIGHT_BLUE],
    4: [
        COLOR_HIGHLIGHT_PINK,
        COLOR_HIGHLIGHT_ORANGE,
        COLOR_HIGHLIGHT_TURQUOISE,
        COLOR_HIGHLIGHT_BLUE,
    ],
    5: [
        COLOR_HIGHLIGHT_PINK,
        COLOR_HIGHLIGHT_ORANGE,
        COLOR_HIGHLIGHT_PURPLE,
        COLOR_HIGHLIGHT_TURQUOISE,
        COLOR_HIGHLIGHT_BLUE,
    ],
    6: [
        COLOR_HIGHLIGHT_PINK,
        COLOR_HIGHLIGHT_ORANGE,
        COLOR_HIGHLIGHT_YELLOW,
        COLOR_HIGHLIGHT_PURPLE,
        COLOR_HIGHLIGHT_TURQUOISE,
        COLOR_HIGHLIGHT_BLUE,
    ],
};

export function getColorForSpecificValues(
    scenarioId: string,
    scenarioListLength: number,
    isAlert?: boolean
): string {
    if (scenarioId === NO_SCENARIO_ID) {
        return "transparent";
    }
    if (isAlert === true) {
        return "white";
    }

    const colorList = MAPPER_COLOR_HIGHLIGHT[scenarioListLength];
    return colorList[scenarioId];
}

const COLOR_HIGHLIGHT_BORDER_PINK = "#A83861";
const COLOR_HIGHLIGHT_BORDER_ORANGE = "#D86303";
const COLOR_HIGHLIGHT_BORDER_YELLOW = "#DCB400";
const COLOR_HIGHLIGHT_BORDER_PURPLE = "#7E31DE";
const COLOR_HIGHLIGHT_BORDER_TURQUOISE = "#1D8277";
const COLOR_HIGHLIGHT_BORDER_BLUE = "#3448E6";
const COLOR_HIGHLIGHT_BORDER_GREY = "#7a85a2";
const COLOR_HIGHLIGHT_BORDER_WARNING = "#F44336";

const MAPPER_COLOR_HIGHLIGHT_BORDER = {
    0: [],
    1: [COLOR_HIGHLIGHT_BORDER_PINK],
    2: [COLOR_HIGHLIGHT_BORDER_PINK, COLOR_HIGHLIGHT_BORDER_BLUE],
    3: [COLOR_HIGHLIGHT_BORDER_PINK, COLOR_HIGHLIGHT_BORDER_ORANGE, COLOR_HIGHLIGHT_BORDER_BLUE],
    4: [
        COLOR_HIGHLIGHT_BORDER_PINK,
        COLOR_HIGHLIGHT_BORDER_ORANGE,
        COLOR_HIGHLIGHT_BORDER_TURQUOISE,
        COLOR_HIGHLIGHT_BORDER_BLUE,
    ],
    5: [
        COLOR_HIGHLIGHT_BORDER_PINK,
        COLOR_HIGHLIGHT_BORDER_ORANGE,
        COLOR_HIGHLIGHT_BORDER_PURPLE,
        COLOR_HIGHLIGHT_BORDER_TURQUOISE,
        COLOR_HIGHLIGHT_BORDER_BLUE,
    ],
    6: [
        COLOR_HIGHLIGHT_BORDER_PINK,
        COLOR_HIGHLIGHT_BORDER_ORANGE,
        COLOR_HIGHLIGHT_BORDER_YELLOW,
        COLOR_HIGHLIGHT_BORDER_PURPLE,
        COLOR_HIGHLIGHT_BORDER_TURQUOISE,
        COLOR_HIGHLIGHT_BORDER_BLUE,
    ],
};

export function getBorderColorForSpecificValues(
    scenarioId: string,
    scenarioListLength: number,
    isAlert?: boolean
): string {
    if (scenarioId === NO_SCENARIO_ID) {
        return COLOR_HIGHLIGHT_BORDER_GREY;
    }
    if (isAlert === true) {
        return COLOR_HIGHLIGHT_BORDER_WARNING;
    }
    const colorList = MAPPER_COLOR_HIGHLIGHT_BORDER[scenarioListLength];
    return colorList[scenarioId];
}

/**
 * Format hour to display on heat map
 * @param hour hour to format: number
 * @returns string
 */
export function formatHeatMapHour(
    hour: number,
    byHalfHours: boolean,
    translateCommon: TFunction
): string {
    if (hour === 24) {
        return translateCommon("dates.dp1-suffix");
    }

    const hourModulo = hour % 24;

    if (byHalfHours) {
        if (hourModulo % 1 !== 0) {
            const plainHour = Math.floor(hourModulo);
            return `${plainHour < 10 ? "0" : ""}${plainHour}:30`;
        }
        return `${hourModulo < 10 ? "0" : ""}${hourModulo}:00`;
    }

    return `${hourModulo < 10 ? "0" : ""}${hourModulo}`;
}

interface HourlyMetricsMinMaxHour {
    minHour: number;
    maxHour: number;
}

/**
 * Compute start and end time for dashboard graph
 * @param hourlyMetricsByHourAndDows array on metrics to filter: HourlyMetricsByHourAndDow[]
 * @returns min and max time
 */
export function computeMinMaxHour(
    hourlyMetricsByHourAndDows: HourlyMetricsByHourAndDow[],
    dows: number[],
    stepMin = 1,
    stepMax = 2,
    byHalfHour = false
): HourlyMetricsMinMaxHour {
    // Loop through all days

    // Handle no data to display
    const isNoData = hourlyMetricsByHourAndDows.every((item) => item.inclvat === 0);
    if (isNoData) {
        return {
            minHour: 12,
            maxHour: 20.5,
        };
    }

    const { min: minValue, max: maxValue } = dows.reduce(
        (acc, dow) => {
            const hourlyMetricsPerDay = hourlyMetricsByHourAndDows.filter(
                (hourlyMetric) => hourlyMetric.shiftedDow === dow
            );
            const minHours = minBy(
                hourlyMetricsPerDay.filter((hourlyMetricPerDay) =>
                    byHalfHour ? hourlyMetricPerDay.inclvat > 0 : hourlyMetricPerDay.countTicket > 0
                ),
                "localHour"
            );
            const maxHours = maxBy(
                hourlyMetricsPerDay.filter((hourlyMetricPerDay) =>
                    byHalfHour ? hourlyMetricPerDay.inclvat > 0 : hourlyMetricPerDay.countTicket > 0
                ),
                "localHour"
            );
            if (minHours?.localHour < acc.min) {
                acc.min = minHours.localHour;
            }
            if (maxHours?.localHour > acc.max) {
                acc.max = maxHours.localHour;
            }
            return acc;
        },
        // min set to 24 to go from biggest possible hour
        { min: 24, max: 0 }
    );

    // add 2 on maxHour for last hour to be taken in range
    return {
        minHour: max([minValue - stepMin, 5]),
        maxHour: min([maxValue + stepMax, 29]),
    };
}
